import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from '../../../../hooks';
import proofkitFilemakerService from '../../../../services/proofkitFilemakerService';
import { ArrowLeft, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Label, Textarea, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui';

const TEMPLATES_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_TEMPLATES || "templates";

export default function TemplatesForm() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    subject: "",
    tipo: "email",
    descripcion: "",
    html: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const nombre = String(formData?.nombre || "").trim();
    const subject = String(formData?.subject || "").trim();
    const html = String(formData?.html || "").trim();

    if (!nombre) {
      return toast({ title: "Falta nombre", description: "Debes indicar el nombre de la plantilla", variant: "destructive" });
    }

    if (!subject) {
      return toast({ title: "Falta asunto", description: "Debes indicar el asunto de la plantilla", variant: "destructive" });
    }

    if (!html) {
      return toast({ title: "Falta contenido", description: "Debes indicar el contenido de la plantilla", variant: "destructive" });
    }

    try {
      setSaving(true);

      await proofkitFilemakerService.createRecord(TEMPLATES_LAYOUT, {
        nombre,
        subject,
        tipo: formData.tipo,
        descripcion: String(formData.descripcion || "").trim(),
        html,
      });

      toast({ title: "Plantilla creada", description: "La plantilla fue creada exitosamente" });
      navigate("/campanas/templates");
    } catch (err) {
      console.error("Error creating template:", err);
      toast({ title: "Error", description: "No se pudo crear la plantilla", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate("/campanas/templates")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
        <Button onClick={onSubmit} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Guardando..." : "Crear Plantilla"}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Nueva Plantilla</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-6" onSubmit={onSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre *</Label>
                <Input
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Asunto *</Label>
                <Input
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={formData.tipo} onValueChange={(v) => handleSelectChange("tipo", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="notificacion">Notificación</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                name="descripcion"
                value={formData.descripcion}
                onChange={handleInputChange}
                rows={3}
                placeholder="Descripción breve de la plantilla..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="html">Contenido HTML *</Label>
              <Textarea
                id="html"
                name="html"
                value={formData.html}
                onChange={handleInputChange}
                rows={10}
                className="font-mono text-sm"
                placeholder="Contenido HTML de la plantilla... Puedes usar variables como {{nombre}}, {{empresa}}, etc."
                required
              />
              <p className="text-sm text-muted-foreground">
                Puedes usar variables dinámicas como: {"{{nombre}}"}, {"{{email}}"}, {"{{empresa}}"}, etc.
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
